import React, { useState, useEffect, useCallback, Fragment } from 'react';
import { useDispatch } from 'react-redux';

import { useDropzone } from 'react-dropzone';
import { Box, Chip, MenuItem, Select, Typography } from '@mui/material';

import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import PrimaryButton from '../ui/buttons/primaryButton';
import { uploadFile } from '../../helpers/storageHelper';
import { uploadPlayers } from '../../features/player/playerSlice';
import playerService from '../../features/player/playerService';
import { useAlert } from '../../contexts/alertContext';

const IMPORT_METHOD = {
  EXCEL: 'excel',
  BOWLS_MASTER: 'bowlsMaster'
};

// Drag-and-drop Excel (.xlsx) importer for players/teams/sides. Renders the
// "Download Template" / "Upload Template" steps plus an import-method
// picker - callers decide how to present it (inline, in a dialog, etc).
// Requires a persisted event to attach the imported players/teams to.
const ExcelPlayersImport = ({ eventId, onImported }) => {
  const dispatch = useDispatch();
  const { showAlertError } = useAlert();

  const [importMethod, setImportMethod] = useState(IMPORT_METHOD.EXCEL);
  const [droppedFile, setDroppedFile] = useState();
  const [importing, setImporting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  useEffect(() => {
    if (droppedFile) {
      importFile(droppedFile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [droppedFile]);

  const onDrop = useCallback(async (files) => {
    setDroppedFile(files[0]);
  }, []);

  const importFile = async (fileData) => {
    setImporting(true);
    try {
      const { fileName } = await uploadFile(fileData);

      if (fileName) {
        const result = await dispatch(uploadPlayers({ fileName, eventId })).unwrap();
        onImported?.(result);
      }
    } catch (error) {
      showAlertError?.(typeof error === 'string' ? error : 'Failed to import players. Please try again.');
    } finally {
      setImporting(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'xlsx',
    disabled: !eventId || importing
  });

  const handleDownloadTemplate = async () => {
    setDownloadingTemplate(true);
    try {
      const blob = await playerService.downloadTemplate(eventId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'player-import-template.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      showAlertError?.('Unable to download the template. Please try again.');
    } finally {
      setDownloadingTemplate(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Select
        size="small"
        value={importMethod}
        onChange={(e) => setImportMethod(e.target.value)}
        sx={{ maxWidth: 320 }}>
        <MenuItem value={IMPORT_METHOD.EXCEL}>Excel Spreadsheet</MenuItem>
        <MenuItem value={IMPORT_METHOD.BOWLS_MASTER} disabled>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography>Import from Bowls Master</Typography>
            <Chip label="Coming Soon" size="small" />
          </Box>
        </MenuItem>
      </Select>

      {importMethod === IMPORT_METHOD.EXCEL ? (
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2}>
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Step 1: Download Template
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
              Download the Excel template, then fill in your team and player details.
            </Typography>
            <PrimaryButton
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon fontSize="small" />}
              onClick={handleDownloadTemplate}
              disabled={!eventId || downloadingTemplate}>
              {downloadingTemplate ? 'Preparing...' : 'Download Template'}
            </PrimaryButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              p: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1
            }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Step 2: Upload Template
            </Typography>
            <Box
              {...getRootProps()}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              sx={{
                p: 3,
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 1,
                cursor: eventId ? 'pointer' : 'not-allowed',
                opacity: eventId ? 1 : 0.5
              }}>
              {importing || (droppedFile && !droppedFile.path) ? (
                <Fragment>
                  <Typography>Importing...</Typography>
                </Fragment>
              ) : droppedFile ? (
                <>
                  <Typography>Uploaded File</Typography>
                  <Typography>{droppedFile.name}</Typography>
                </>
              ) : (
                <>
                  <input {...getInputProps({ multiple: false })} />
                  <CloudUploadIcon fontSize="large" />
                  {isDragActive ? (
                    <Typography>Drop the file here ...</Typography>
                  ) : (
                    <Typography textAlign="center">
                      Drag &apos;n&apos; drop your completed Excel (.xlsx) file here, or click to
                      select a file
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>
        </Box>
      ) : (
        <Box sx={{ p: 2, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Importing players from Bowls Master is coming soon.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ExcelPlayersImport;
